// src/modules/units/tyres/views/retread-batch/retreadbatchpicker.view.js
//
// Pure rendering for the "add tyres to a draft" picker — a filter box, a table of
// selectable in-stock tyres, and a footer with the running count. Data in, HTML
// out; no fetch, no DOM. The orchestrator owns selection/filter state and the
// eligibility errors it marks after a rejected add.
//
// Retread count is deliberately not a column: the tyre list doesn't reliably carry
// it, and the authoritative cap signal is the backend's RETREAD_CAP_REACHED
// warning on add — shown in the message line, not guessed per row.

import { esc } from '../../shared/tyre-format.js';

const MUTED = '<span class="tm-muted">—</span>';
const PICKER_COLSPAN = 3;

function specOf(t) {
  const brand = t?.tyreBrand || t?.tyreMake || '';
  const size = t?.tyreSize || '';
  return [brand, size].filter(Boolean).join(' ');
}

/** One selectable candidate row. `errorText` marks a tyre a prior add rejected;
 *  `disabled` + `noteText` marks a tyre that can't be picked (e.g. on another
 *  draft batch) — the checkbox is greyed and a quiet note explains why. */
export function pickerRowHTML(tyre = {}, { checked = false, disabled = false, noteText = '', errorText = '' } = {}) {
  const id = tyre?.externalTyreId ?? '';
  const spec = specOf(tyre);
  const err = errorText ? `<div class="tm-rb-pick-err">${esc(errorText)}</div>` : '';
  const note = (!errorText && noteText) ? `<div class="tm-rb-pick-note">${esc(noteText)}</div>` : '';
  const rowCls = errorText ? ' class="tm-rb-pick-bad"' : (disabled ? ' class="tm-rb-pick-off"' : '');
  return `
    <tr${rowCls} data-pick-row data-tyre-id="${esc(id)}">
      <td class="tm-rb-pick-check">
        <input type="checkbox" data-pick-check value="${esc(id)}"${checked ? ' checked' : ''}${disabled ? ' disabled' : ''} aria-label="Select tyre ${esc(id)}" />
      </td>
      <td><b>${esc(id)}</b>${err}${note}</td>
      <td>${spec ? esc(spec) : MUTED}</td>
    </tr>`;
}

export function pickerStateRowHTML(message) {
  return `
    <tr>
      <td class="tm-state-td" colspan="${PICKER_COLSPAN}">
        <span class="tm-state"><span class="tm-state-msg">${esc(message)}</span></span>
      </td>
    </tr>`;
}

export function pickerHTML({ reference = '', selectedCount = 0 } = {}) {
  const ref = reference ? esc(reference) : '';
  return `
    <div class="tm-rb-picker">
      <div class="tm-form-head tm-rb-picker-head">
        <div>
          <h2 class="tm-form-title">Add tyres${ref ? ` to ${ref}` : ''}</h2>
          <p class="tm-form-sub">Pick in-stock tyres to send for retread. Shows the tyres in the yard you have selected.</p>
        </div>
        <div class="tm-rb-picker-actions">
          <span class="tm-rb-picker-count" data-pick-count>${Number(selectedCount) || 0} selected</span>
          <button type="button" class="tm-btn-secondary" data-pick-cancel>Cancel</button>
          <button type="button" class="tm-btn-primary" data-pick-add disabled>Add selected</button>
        </div>
      </div>

      <div class="tm-form-msg" data-pick-msg hidden></div>

      <input type="search" class="tm-input tm-rb-picker-search" data-pick-search
             placeholder="Filter by tyre ID, brand or size…" aria-label="Filter tyres" />

      <div class="tm-table-wrap">
        <table class="tm-table tm-rb-picker-table">
          <thead>
            <tr><th class="tm-rb-pick-check"></th><th>Tyre</th><th>Brand / Size</th></tr>
          </thead>
          <tbody data-pick-rows></tbody>
        </table>
      </div>
    </div>`;
}
